/* SpawnAnything — unit recipe ("Bauplan") schema, validator/repairer and safe compile guard.
   Pure module (only imports the option lists of the pure face/hair modules), runs in the browser and in Node.

   Two input shapes are accepted by validateUnitRecipe():
   1. the game's own format (exactly what RECIPES in voxel.js uses), and
   2. the compact "wire" format the LLM produces under UNIT_SCHEMA: colours and add entries are
      one short string each (see WIRE FORMAT below), which keeps 30-40 parts at ~1/4 of the output
      tokens of JSON objects. The older object wire format (every key present, pattern/color2/period)
      is still accepted.
   The output is always the game's format, with defaults stripped, and is guaranteed to
   compile with recipeModel() inside the voxel budget. */

import { KAMPF } from './combatRole.js';
import { FACE_OPTIONS, FACE_COLOR_KEYS, MAX_MARKS } from './faces.js';
import { HAIR_STYLES, HAT_TYPES } from './hair.js';

export const UNIT_TEMPLATES = {
  humanoid: {
    parts: ['head', 'torso', 'pelvis', 'arms', 'armL', 'armR', 'legs', 'legL', 'legR'],
    colors: ['skin', 'torso', 'pelvis', 'arms', 'hands', 'legs', 'feet'],
    build: { width: [0.6, 1.8], headScale: [0.6, 1.8], height: [0.7, 1.4], armForward: 'bool' },
  },
  serpent: {
    parts: ['head', 'jaw', 'segments', 'tail'],
    colors: ['body', 'belly', 'head', 'jaw'],
    build: { length: [4, 14], radius: [0.4, 1.3], segments: [3, 9, 'int'] },
  },
  quadruped: {
    parts: ['body', 'head', 'neck', 'jaw', 'legs', 'legFL', 'legFR', 'legBL', 'legBR', 'tail'],
    colors: ['body', 'belly', 'head', 'neck', 'jaw', 'legs', 'hooves', 'feet', 'tail'],
    // see templates/quadruped.js: length/height in metres; legLength/neckLength as fractions (> 1 = metres, see METRES);
    // the rest multipliers. neckLength/tail 0 = none, neckAngle 0 = horizontal (see ZERO_SNAP).
    build: { length: [0.6, 6], height: [0.4, 6], legLength: [0.2, 0.8], neckLength: [0.02, 0.6], bulk: [0.4, 2], headScale: [0.4, 1.8],
      jaw: [0.3, 1.8], tail: [0.02, 2], neckAngle: [-20, 90], headShape: ['box', 'ellipsoid'] },
  },
};
export const TEMPLATE_NAMES = Object.keys(UNIT_TEMPLATES);
export const SHAPES = ['box', 'ellipsoid', 'cylinder', 'cone'];
export const MAX_ADD = 40;
export const MAX_NAME = 40;
export const AT_RANGE = [-6, 6];
export const SIZE_RANGE = [0.02, 6];
const ALL_PARTS = [...new Set(Object.values(UNIT_TEMPLATES).flatMap(t => t.parts))];
const ALL_COLOR_KEYS = [...new Set(Object.values(UNIT_TEMPLATES).flatMap(t => t.colors))];
const ALL_BUILD_KEYS = [...new Set(Object.values(UNIT_TEMPLATES).flatMap(t => Object.keys(t.build)))];
const PATTERNS = ['none', 'checker', 'stripes_x', 'stripes_y', 'stripes_z'];

/* ---------------- WIRE FORMAT (compact strings) ----------------
   colors entry: "<part> <colour> [checker:<colour2> | stripes:<colour2>]"
     e.g. "torso #828a94 checker:#646b75"
   add entry:    "<on> <shape> <atX> <atY> <atZ> <sizeX> <sizeY> <sizeZ> <colour> [flags...]"
     flags: mirror | glow | rot:<x>,<y>,<z> | axis:x|z | checker:<colour2> | stripes:<x|y|z>:<colour2>[:<period>]
     e.g. "head box 0.4 0.08 0.97 0.24 0.16 0.06 #f4f4f2 mirror"
          "torso box 0 0.1 1.03 0.05 0.75 0.05 #3e4a26 stripes:y:#2b2e33:0.066"
   <colour> is '#rrggbb' or 'team'. Numbers may also be written "0.4,0.08,0.97". */
const WIRE_FLAGS = 'mirror | glow | rot:x,y,z | axis:x|z | checker:<c2> | stripes:<axis>:<c2>[:<period>]';
const wireNum = t => { const n = toNumLoose(t); return n === null ? null : n; };
function toNumLoose(v) {
  if (typeof v !== 'string' || v === '' || v.length > 24) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
/** Parse one compact colour entry -> { part, color, pattern, color2 } (object wire format). */
function parseWireColor(str) {
  const t = String(str).trim().split(/\s+/).slice(0, 4);
  const o = { part: t[0] || '', color: t[1], pattern: 'none', color2: t[1] };
  const f = t[2] || '';
  const m = /^(checker|stripes(?:_[xyz])?)[:=](?:[xyz]:)?(.+)$/.exec(f);
  if (m) { o.pattern = m[1] === 'checker' ? 'checker' : 'stripes_y'; o.color2 = m[2]; }
  return o;
}
/** Parse one compact add entry -> object wire entry; problems are pushed to `errors` (never throws). */
function parseWireAdd(str, path, errors) {
  const s = String(str).slice(0, 400).replace(/[\[\]{}()"]/g, ' ').trim();
  // split, then flatten "1,2,3" groups that are not flags
  const raw = s.split(/\s+/), tok = [];
  for (const t of raw) { if (!t.includes(':') && t.includes(',')) tok.push(...t.split(',').filter(Boolean)); else tok.push(t); }
  const o = { on: tok[0] || '', shape: tok[1] || 'box', at: [0, 0, 0], size: [0.3, 0.3, 0.3], rot: [0, 0, 0], axis: 'y', color: undefined, pattern: 'none', color2: undefined, period: 0, mirror: false, glow: false };
  let i = 2;
  // tolerate extra words before the shape ("back pack box ..."): the shape is the last word before the numbers
  if (wireNum(tok[2]) === null) {
    let j = 2;
    while (j < tok.length && j < 6 && wireNum(tok[j]) === null) j++;
    if (j < tok.length && wireNum(tok[j]) !== null) { errors.push(`${path}: extra words before the numbers ignored`); o.shape = tok[j - 1]; i = j; }
  }
  const nums = [];
  while (i < tok.length && nums.length < 6 && wireNum(tok[i]) !== null) nums.push(wireNum(tok[i++]));
  if (nums.length < 6) errors.push(`${path}: expected 6 numbers (at + size), got ${nums.length}`);
  o.at = [0, 1, 2].map(k => nums[k] ?? 0);
  o.size = [3, 4, 5].map(k => nums[k] ?? 0.3);
  if (nums.length === 6 && wireNum(tok[i]) !== null) { errors.push(`${path}: too many numbers, extra ignored`); while (wireNum(tok[i]) !== null) i++; }
  o.color = tok[i++];
  for (; i < tok.length && i < 16; i++) {
    const f = tok[i], lf = f.toLowerCase();
    if (lf === 'mirror' || lf === 'm') o.mirror = true;
    else if (lf === 'glow' || lf === 'g') o.glow = true;
    else if (lf.startsWith('rot:') || lf.startsWith('rot=')) { const r = f.slice(4).split(',').map(wireNum); if (r.length === 3 && r.every(v => v !== null)) o.rot = r; else errors.push(`${path}: bad rot, ignored`); }
    else if (lf.startsWith('axis:') || lf.startsWith('axis=')) o.axis = lf.slice(5);
    else if (lf.startsWith('checker:') || lf.startsWith('checker=')) { o.pattern = 'checker'; o.color2 = f.slice(8); }
    else if (lf.startsWith('stripes')) {
      // stripes:<axis>:<colour>[:<period>]  (also stripes_y:<colour>[:<period>])
      const parts = f.split(/[:=]/);
      let ax = 'y';
      const head = parts.shift().toLowerCase();
      if (/^stripes_[xyz]$/.test(head)) ax = head.slice(-1);
      if (parts.length && /^[xyz]$/i.test(parts[0])) ax = parts.shift().toLowerCase();
      o.pattern = 'stripes_' + ax; o.color2 = parts[0]; o.period = wireNum(parts[1] ?? '') ?? 0;
    } else errors.push(`${path}: unknown flag '${f.slice(0, 20)}' ignored (allowed: ${WIRE_FLAGS})`);
  }
  if (o.color2 === undefined) o.color2 = o.color;
  return o;
}

/* ---------------- JSON schema for structured output (the "wire" format) ----------------
   Only features the structured-output API supports: objects with additionalProperties:false,
   every property required, enums, arrays, numbers, strings, booleans. Ranges are enforced by
   validateUnitRecipe(), not by the schema. */
const num = { type: 'number' };
const vec3 = { type: 'array', items: num };
const colorStr = { type: 'string', description: "'#rrggbb' or 'team'" };
const buildProps = Object.fromEntries(ALL_BUILD_KEYS.map(k => [k, k === 'armForward' ? { type: 'boolean' } : k === 'headShape' ? { type: 'string', enum: ['box', 'ellipsoid'] } : num]));
/* ---- face / hair / hat (Gesichts-Baukasten): enumerations only; 'auto' / '' = template default ---- */
const FACE_ENUM_KEYS = Object.keys(FACE_OPTIONS).filter(k => k !== 'marks');
const optColor = { type: 'string', description: "'#rrggbb' or '' (= Standard)" };
export const FACE_SCHEMA = {
  type: 'object', additionalProperties: false, required: [...FACE_ENUM_KEYS, 'marks', ...FACE_COLOR_KEYS],
  properties: {
    ...Object.fromEntries(FACE_ENUM_KEYS.map(k => [k, { type: 'string', enum: ['auto', ...FACE_OPTIONS[k]] }])),
    marks: { type: 'array', items: { type: 'string', enum: FACE_OPTIONS.marks }, description: `0-${MAX_MARKS} entries` },
    ...Object.fromEntries(FACE_COLOR_KEYS.map(k => [k, optColor])),
  },
};
export const HAIR_SCHEMA = { type: 'object', additionalProperties: false, required: ['style', 'color'], properties: { style: { type: 'string', enum: HAIR_STYLES }, color: optColor } };
export const HAT_SCHEMA = { type: 'object', additionalProperties: false, required: ['type', 'color', 'color2'], properties: { type: { type: 'string', enum: HAT_TYPES }, color: { type: 'string', description: "'#rrggbb', 'team' or ''" }, color2: { type: 'string', description: "'#rrggbb', 'team' or ''" } } };

export const UNIT_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['name', 'template', 'kampf', 'build', 'colors', 'face', 'hair', 'hat', 'mount', 'add'],
  properties: {
    name: { type: 'string' },
    kampf: { type: 'string', enum: KAMPF, description: 'how the figure fights (src/game/combatRole.js)' },
    face: FACE_SCHEMA,
    hair: HAIR_SCHEMA,
    hat: HAT_SCHEMA,
    mount: {
      type: 'object', additionalProperties: false, required: ['on', 'at'],
      description: "quadruped only: where the weapon sits ('none' = mouth)",
      properties: { on: { type: 'string', enum: ['none', 'body', 'head'] }, at: vec3 },
    },
    template: { type: 'string', enum: TEMPLATE_NAMES },
    build: { type: 'object', additionalProperties: false, required: ALL_BUILD_KEYS, properties: buildProps },
    colors: {
      type: 'array',
      items: { type: 'string', description: "'<part> <#rrggbb|team> [checker:<#c2>|stripes:<#c2>]'" },
    },
    add: {
      type: 'array',
      items: { type: 'string', description: "'<on> <shape> <atX> <atY> <atZ> <sizeX> <sizeY> <sizeZ> <#rrggbb|team> [" + WIRE_FLAGS + "]'" },
    },
  },
};

/* ---------------- small helpers (all total: never throw) ---------------- */
const has = (o, k) => typeof k === 'string' && Object.prototype.hasOwnProperty.call(o, k);
const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const toNum = v => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '' && v.length < 32) { const n = Number(v); return Number.isFinite(n) ? n : null; }
  return null;
};
const round4 = v => Math.round(v * 1e4) / 1e4 || 0; // also turns -0 into 0
const NAMED = {
  red: '#d8443c', rot: '#d8443c', blue: '#3c78d8', blau: '#3c78d8', green: '#3fae5a', gruen: '#3fae5a', 'grün': '#3fae5a',
  yellow: '#f2d23c', gelb: '#f2d23c', black: '#16161b', schwarz: '#16161b', white: '#f4f4f2', weiss: '#f4f4f2', 'weiß': '#f4f4f2',
  gray: '#888888', grey: '#888888', grau: '#888888', orange: '#f0802a', purple: '#7a3ab8', lila: '#7a3ab8', violett: '#7a3ab8',
  pink: '#f27ab0', rosa: '#f2a1b5', brown: '#6b4426', braun: '#6b4426', gold: '#d9b23c', silver: '#c6cdd5', silber: '#c6cdd5',
  teal: '#1f8a8a', cyan: '#3af0ff', skin: '#e3b48a', haut: '#e3b48a',
};
/** Parse a colour; returns canonical '#rrggbb' | 'team' | 'team2' | null. */
export function parseColor(v) {
  if (typeof v !== 'string' || v.length > 24) return null;
  const s = v.trim().toLowerCase();
  if (s === 'team' || s === 'team2') return s;
  let m = /^#?([0-9a-f]{6})$/.exec(s);
  if (m) return '#' + m[1];
  m = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(s);
  if (m) return '#' + m[1] + m[1] + m[2] + m[2] + m[3] + m[3];
  m = /^0x([0-9a-f]{6})$/.exec(s);
  if (m) return '#' + m[1];
  return has(NAMED, s) ? NAMED[s] : null;
}
function vec(v, def, lo, hi) {
  const src = Array.isArray(v) ? v : typeof v === 'number' ? [v, v, v] : null;
  if (!src) return { v: def.slice(), bad: v !== undefined };
  let bad = src.length !== 3;
  const out = [0, 1, 2].map(i => {
    const n = toNum(src[i]);
    if (n === null) { bad = true; return def[i]; }
    const c = clamp(n, lo, hi);
    if (c !== n) bad = true;
    return c;
  });
  return { v: out, bad };
}
function cleanName(v, fallback) {
  let s = typeof v === 'string' ? v : '';
  s = s.replace(/[\u0000-\u001f\u007f<>`]/g, '').replace(/\s+/g, ' ').trim();
  if (!s) s = fallback;
  return Array.from(s).slice(0, MAX_NAME).join('');
}

/* `on` names the model might use for another template, mapped to the closest known part */
const ON_ALIAS = {
  humanoid: { body: 'torso', chest: 'torso', back: 'torso', backpack: 'torso', belly: 'torso', neck: 'head', jaw: 'head', face: 'head', hair: 'head', hat: 'head', segments: 'torso', tail: 'pelvis', hip: 'pelvis', hips: 'pelvis', arm: 'arms', hand: 'arms', hands: 'arms', leg: 'legs', feet: 'legs', foot: 'legs', handL: 'armL', handR: 'armR', legsL: 'legL', legsR: 'legR' },
  serpent: { body: 'segments', torso: 'segments', segment: 'segments', neck: 'head', face: 'head', pelvis: 'tail', legs: 'segments', arms: 'segments', mouth: 'jaw' },
  quadruped: { torso: 'body', pelvis: 'body', chest: 'body', segments: 'body', arms: 'legs', armL: 'legs', armR: 'legs', legL: 'legs', legR: 'legs', leg: 'legs', feet: 'legs', face: 'head', mouth: 'jaw' },
};
const SHAPE_ALIAS = { cube: 'box', block: 'box', quader: 'box', sphere: 'ellipsoid', ball: 'ellipsoid', kugel: 'ellipsoid', ellipse: 'ellipsoid', tube: 'cylinder', zylinder: 'cylinder', pyramid: 'cone', kegel: 'cone', spike: 'cone' };

/* ---------------- voxel budget estimate ----------------
   Approximate half-extent of each part's frame in voxels (max over the realism slider t and
   default build). Used to keep the total voxel count of a recipe under the compile limit. */
const HUM_FRAME = { head: [3.3, 3.2, 3.1], torso: [4.3, 5.3, 2.6], pelvis: [4.1, 1.6, 2.5], arm: [1.4, 6.7, 1.4], leg: [1.8, 8.8, 2] };
const FILL = { box: 1, cylinder: 0.8, ellipsoid: 0.56, cone: 0.42 };
const SERP_VS = 0.14;
// mirrors quadrupedFrames()/quadrupedVoxelSize() in templates/quadruped.js (half extents in metres).
// Kept as a copy because this module has no imports (runs without three.js).
function quadFrames(b) {
  const L = b.length ?? 3, H = b.height ?? 2;
  const legRaw = b.legLength ?? 0.5, neckRaw = b.neckLength ?? 0.2;
  const legF = clamp(legRaw > 1 ? legRaw / H : legRaw, 0.2, 0.8), neckF = Math.max(0, neckRaw > 1 ? neckRaw / L : neckRaw);
  const bulk = (b.bulk ?? 1) * (b.width ?? 1), hs = b.headScale ?? 1, js = b.jaw ?? 1, tailF = b.tail ?? 1;
  const legH = H * legF, bhy = (H - legH) / 2, bhz = L / 2, bhx = Math.min(bhy * 0.9 * bulk, L * 0.38);
  const lw = Math.max(0.05, bhx * 0.3), ov = bhy * 0.6;
  const nLen = neckF * L, nr = bhy * 0.42;
  const hh = [bhx * 0.62 * hs, bhy * 0.6 * hs, bhy * 0.75 * hs];
  const tl = L * 0.3 * tailF, tr = Math.max(0.04, bhx * 0.12), td = 35 * Math.PI / 180;
  const nh = [bhx * 0.5, nLen / 2 + nr * 0.6, nr], nd = (nh[1] + nh[2]) * 0.75, hasNeck = nLen > 0.05;
  return {
    vs: clamp(Math.max(L, H) / 38, 0.066, 0.16),
    body: [bhx, bhy, bhz], leg: [lw, (legH + ov) / 2, lw * 1.05], head: hh, jaw: [hh[0] * 0.8 * js, hh[1] * 0.28 * js, hh[2] * 0.85 * js],
    neck: hasNeck ? [nh[0], nd, nd] : hh, neckCyl: nh, hasNeck,
    tail: [tr, Math.sin(td) * tl / 2 + tr, Math.cos(td) * tl / 2 + tr], tailCore: [tr, tr, tl / 2], hasTail: tl > 0.05,
  };
}
function frameInfo(rec, on) {
  const b = rec.build || {};
  if (rec.template === 'serpent') {
    const L = b.length || 10, r = b.radius || 0.8, n = Math.min(9, b.segments || 8), k = 1 / SERP_VS;
    if (on === 'head') return { h: [0.95 * r * k, 0.72 * r * k, 1.15 * r * k], mult: 1 };
    if (on === 'jaw') return { h: [0.82 * r * k, 0.26 * r * k, r * k], mult: 1 };
    if (on === 'tail') return { h: [0.45 * r * k, 0.45 * r * k, 0.64 * L / n * k], mult: 1 };
    return { h: [r * k, r * k, 0.64 * L / n * k], mult: n };
  }
  if (rec.template === 'quadruped') {
    const Q = quadFrames(b), k = 1 / Q.vs;
    const sc = h => h.map(x => x * k);
    switch (on) {
      case 'legs': return { h: sc(Q.leg), mult: 4 };
      case 'legFL': case 'legFR': case 'legBL': case 'legBR': return { h: sc(Q.leg), mult: 1 };
      case 'head': return { h: sc(Q.head), mult: 1 };
      case 'jaw': return { h: sc(Q.jaw), mult: 1 };
      case 'neck': return { h: sc(Q.neck), mult: 1 };
      case 'tail': return { h: sc(Q.tail), mult: 1 };
      default: return { h: sc(Q.body), mult: 1 };
    }
  }
  const f = Math.max(1, b.width || 1, b.height || 1), hs = Math.max(1, b.headScale || 1);
  const s = v => v.map(x => x * f);
  switch (on) {
    case 'head': return { h: HUM_FRAME.head.map(x => x * hs), mult: 1 };
    case 'torso': return { h: s(HUM_FRAME.torso), mult: 1 };
    case 'pelvis': return { h: s(HUM_FRAME.pelvis), mult: 1 };
    case 'arms': return { h: s(HUM_FRAME.arm), mult: 2 };
    case 'armL': case 'armR': return { h: s(HUM_FRAME.arm), mult: 1 };
    case 'legs': return { h: s(HUM_FRAME.leg), mult: 2 };
    default: return { h: s(HUM_FRAME.leg), mult: 1 };
  }
}
function entryCost(rec, a) {
  const { h, mult } = frameInfo(rec, a.on);
  const size = a.size || [1, 1, 1]; // game format: absent size = the whole part frame
  const e = [0, 1, 2].map(i => Math.max(0.55, size[i] * h[i]));
  const cells = (2 * e[0] + 1.5) * (2 * e[1] + 1.5) * (2 * e[2] + 1.5) * (FILL[a.shape] || 1);
  let cost = cells;
  if (a.rot) { const r = Math.hypot(e[0], e[1], e[2]); cost = Math.max(cells, Math.pow(2 * r + 2, 3) / 60); } // bbox iterations
  return cost * mult * (a.mirror ? 2 : 1);
}
function baseCost(rec) {
  const b = rec.build || {};
  // painted face, hair and headgear add a few hundred voxels on the far LOD (the fine head layer is not counted)
  const headExtra = (rec.hair ? 900 : 0) + (rec.hat ? 1200 : 0) + (rec.face ? 300 : 0);
  return headExtra + baseCostBody(rec, b);
}
function baseCostBody(rec, b) {
  if (rec.template === 'serpent') return 760 * (b.length || 10) * Math.pow(b.radius || 0.8, 2) + 600;
  if (rec.template === 'quadruped') {
    // volume of each base primitive (padded by half a voxel), fill factor per shape. Calibrated against
    // quadrupedModel() over random builds: always 1.25x-5x the real count (an upper bound, tight for big beasts).
    const Q = quadFrames(b), p = Q.vs * 0.5, hm = Q.vs * 0.55;
    const vol = (h, f) => 8 * f * (Math.max(h[0], hm) + p) * (Math.max(h[1], hm) + p) * (Math.max(h[2], hm) + p) / Math.pow(Q.vs, 3);
    return vol(Q.body, 0.7) + 4 * vol(Q.leg, 1.05) + vol(Q.head, 1) + vol(Q.jaw, 1)
      + (Q.hasNeck ? vol(Q.neckCyl, 0.8) : 0) + (Q.hasTail ? vol(Q.tailCore, 0.55) : 0) + 100;
  }
  const f = Math.max(1, b.width || 1, b.height || 1, b.headScale || 1);
  return 4200 * f * f;
}
/** Rough upper-bound voxel estimate for a normalized recipe (compile cost guard). */
export function estimateUnitVoxels(rec) {
  try {
    let s = baseCost(rec);
    for (const a of rec.add || []) s += entryCost(rec, a);
    return Math.round(s);
  } catch { return Infinity; }
}
export const VOXEL_BUDGET = 52000; // estimate limit; safeCompile's hard limit on the real count is 60000

/* ---------------- normalization ---------------- */
function normColorValue(v, path, errors, fallback) {
  // game format: string | { color, checker?, stripes? }
  if (typeof v === 'string') {
    const c = parseColor(v);
    if (!c) { errors.push(`${path}: invalid colour ${JSON.stringify(v).slice(0, 30)} -> ${fallback}`); return fallback; }
    return c;
  }
  if (isObj(v)) {
    const c = parseColor(v.color);
    if (!c) errors.push(`${path}.color: invalid colour -> ${fallback}`);
    const o = { color: c || fallback };
    if (v.checker !== undefined) { const k = parseColor(v.checker); if (k) o.checker = k; else errors.push(`${path}.checker: invalid colour, dropped`); }
    if (v.stripes !== undefined) { const k = parseColor(typeof v.stripes === 'string' ? v.stripes : isObj(v.stripes) ? v.stripes.color : null); if (k) o.stripes = k; else errors.push(`${path}.stripes: invalid colour, dropped`); }
    return o;
  }
  errors.push(`${path}: missing colour -> ${fallback}`);
  return fallback;
}
function wireColor(color, pattern, color2, period, path, errors, fallback) {
  // wire format: color + pattern + color2 -> game colour value (string | object)
  const c = parseColor(color);
  if (!c) errors.push(`${path}.color: invalid colour -> ${fallback}`);
  const base = c || fallback;
  if (typeof pattern !== 'string' || pattern === 'none' || !PATTERNS.includes(pattern)) return { color: base };
  const c2 = parseColor(color2);
  if (!c2) { errors.push(`${path}.color2: invalid colour, pattern dropped`); return { color: base }; }
  if (pattern === 'checker') return { color: base, checker: c2 };
  const p = toNum(period);
  return { color: base, stripes: { color: c2, axis: pattern.slice(-1), period: p && p > 0 ? round4(clamp(p, 0.02, 0.5)) : 0.05 } };
}

function normColors(input, tpl, errors) {
  const allowed = UNIT_TEMPLATES[tpl].colors, out = {};
  if (Array.isArray(input)) {
    for (let i = 0; i < input.length && i < 64; i++) {
      const e = typeof input[i] === 'string' ? parseWireColor(input[i]) : input[i];
      if (!isObj(e) || typeof e.part !== 'string') { errors.push(`colors[${i}]: not an entry, dropped`); continue; }
      if (!allowed.includes(e.part)) { errors.push(`colors[${i}]: unknown part '${String(e.part).slice(0, 20)}' for ${tpl}, dropped`); continue; }
      const w = wireColor(e.color, e.pattern, e.color2, 0.05, `colors.${e.part}`, errors, '#888888');
      out[e.part] = w.checker ? { color: w.color, checker: w.checker } : w.stripes ? { color: w.color, stripes: w.stripes.color } : w.color;
    }
  } else if (isObj(input)) {
    for (const k of Object.keys(input).slice(0, 64)) {
      if (!allowed.includes(k)) { errors.push(`colors.${k.slice(0, 20)}: unknown for ${tpl}, dropped`); continue; }
      out[k] = normColorValue(input[k], `colors.${k}`, errors, '#888888');
    }
  } else if (input !== undefined) errors.push('colors: not an object, ignored');
  const main = tpl === 'humanoid' ? 'torso' : 'body';
  if (!out[main] && !(tpl === 'humanoid' && out.skin)) {
    out[main] = '#888888';
    errors.push(`colors.${main}: missing, set to grey`);
  }
  return out;
}

// values above 1 are metres in the quadruped compiler (legLength, neckLength): allowed range for that reading
const METRES = { legLength: [1.0001, 5], neckLength: [1.0001, 4] };
// quadruped keys where 0 is a real value (no neck / no tail / horizontal neck), not "use default".
// The game format keeps an explicit 0. The wire format must send 0 for "default", so tiny values
// (|v| below the snap threshold, e.g. 0.01) are the wire spelling of 0 and are canonicalized to 0.
const ZERO_SNAP = { neckLength: 0.02, tail: 0.02, neckAngle: 0.5 };
const WIRE_ZERO = { neckLength: 0.01, tail: 0.01, neckAngle: 0.01 };
function normBuild(input, tpl, errors, isWire) {
  const spec = UNIT_TEMPLATES[tpl].build, out = {};
  if (input === undefined || input === null) return out;
  if (!isObj(input)) { errors.push('build: not an object, ignored'); return out; }
  for (const k of Object.keys(input).slice(0, 32)) {
    const v = input[k];
    if (!has(spec, k)) {
      // the wire format sends every key; defaults for other templates are expected, not errors
      if (!(isWire || v === 0 || v === false || v === null)) errors.push(`build.${k.slice(0, 20)}: not used by ${tpl}, dropped`);
      continue;
    }
    const sp = spec[k];
    if (sp === 'bool') { if (v === true) out[k] = true; else if (v !== false && v !== 0 && v !== null) errors.push(`build.${k}: not a boolean, dropped`); continue; }
    if (typeof sp[0] === 'string') { // enum
      if (sp.includes(v)) { if (!(isWire && v === sp[0])) out[k] = v; } else if (v !== null && v !== '') errors.push(`build.${k}: invalid, dropped`);
      continue;
    }
    const n = toNum(v);
    if (n === null) { if (v !== null) errors.push(`build.${k}: not a number, dropped`); continue; }
    const snap = tpl === 'quadruped' && has(ZERO_SNAP, k) ? ZERO_SNAP[k] : 0;
    if (n === 0) { if (snap && !isWire) out[k] = 0; continue; } // 0 = "use default" (except quadruped zero keys, game format)
    if (snap && Math.abs(n) < snap) { out[k] = 0; continue; }
    const mr = tpl === 'quadruped' && has(METRES, k) && n > 1 ? METRES[k] : null;
    let c = mr ? clamp(n, mr[0], mr[1]) : clamp(n, sp[0], sp[1]);
    if (sp[2] === 'int') c = Math.round(c);
    if (c !== n) errors.push(`build.${k}: ${n} clamped to ${round4(c)}`);
    out[k] = round4(c);
  }
  return out;
}
function normMount(input, tpl, errors) {
  if (input === undefined || input === null) return null;
  if (!isObj(input)) { errors.push('mount: not an object, dropped'); return null; }
  if (input.on === 'none' || input.on === undefined) return null;
  if (tpl !== 'quadruped') { errors.push(`mount: only used by quadruped, dropped`); return null; }
  if (input.on !== 'body' && input.on !== 'head') { errors.push('mount.on: invalid, dropped'); return null; }
  if (input.at === undefined) return { on: input.on }; // template default [0, 0, 1]
  const at = vec(input.at, [0, 0, 1], -2, 2);
  if (at.bad) errors.push('mount.at: repaired/clamped');
  return { on: input.on, at: at.v.map(round4) };
}

/* face / hair / hat: unknown options are dropped (template default), never thrown */
const blankColor = v => v === undefined || v === null || v === '' || v === 'auto' || v === 'none';
function normFace(input, errors) {
  if (input === undefined || input === null) return null;
  if (!isObj(input)) { errors.push('face: not an object, ignored'); return null; }
  const out = {};
  for (const k of Object.keys(input).slice(0, 48)) {
    const v = input[k];
    if (has(FACE_OPTIONS, k) && k !== 'marks') {
      if (v === 'auto' || v === '' || v === null || v === undefined) continue;
      if (typeof v === 'string' && FACE_OPTIONS[k].includes(v)) out[k] = v;
      else errors.push(`face.${k}: unknown option ${JSON.stringify(v).slice(0, 24)}, dropped`);
    } else if (k === 'marks') {
      const arr = Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [];
      const ok = [...new Set(arr.filter(m => typeof m === 'string' && FACE_OPTIONS.marks.includes(m)))];
      if (ok.length < arr.length) errors.push('face.marks: unknown entries dropped');
      if (ok.length > MAX_MARKS) errors.push(`face.marks: more than ${MAX_MARKS}, truncated`);
      if (ok.length) out.marks = ok.slice(0, MAX_MARKS);
    } else if (FACE_COLOR_KEYS.includes(k)) {
      if (blankColor(v)) continue;
      const c = parseColor(v);
      if (c && c !== 'team' && c !== 'team2') out[k] = c; else errors.push(`face.${k}: invalid colour, dropped`);
    } else errors.push(`face.${String(k).slice(0, 20)}: unknown key, dropped`);
  }
  return Object.keys(out).length ? out : null;
}
function normHair(input, errors) {
  if (input === undefined || input === null) return null;
  if (!isObj(input)) { errors.push('hair: not an object, ignored'); return null; }
  if (!HAIR_STYLES.includes(input.style)) { if (input.style !== undefined && input.style !== '') errors.push('hair.style: unknown, dropped'); return null; }
  if (input.style === 'keine' && blankColor(input.color)) return null;
  const out = { style: input.style };
  if (!blankColor(input.color)) { const c = parseColor(input.color); if (c && c !== 'team' && c !== 'team2') out.color = c; else errors.push('hair.color: invalid colour, dropped'); }
  return out;
}
function normHat(input, errors) {
  if (input === undefined || input === null) return null;
  if (!isObj(input)) { errors.push('hat: not an object, ignored'); return null; }
  if (!HAT_TYPES.includes(input.type)) { if (input.type !== undefined && input.type !== '') errors.push('hat.type: unknown, dropped'); return null; }
  if (input.type === 'keiner') return null;
  const out = { type: input.type };
  for (const k of ['color', 'color2']) if (!blankColor(input[k])) { const c = parseColor(input[k]); if (c) out[k] = c; else errors.push(`hat.${k}: invalid colour, dropped`); }
  return out;
}

function normAdd(a, i, tpl, errors, isWire) {
  const path = `add[${i}]`;
  if (typeof a === 'string') { a = parseWireAdd(a, path, errors); isWire = true; }
  if (!isObj(a)) { errors.push(`${path}: not an object, dropped`); return null; }
  const parts = UNIT_TEMPLATES[tpl].parts;
  let on = typeof a.on === 'string' ? a.on : '';
  if (!parts.includes(on)) {
    const al = has(ON_ALIAS[tpl], on) ? ON_ALIAS[tpl][on] : null;
    if (al) { errors.push(`${path}.on: '${on}' mapped to '${al}'`); on = al; }
    else { errors.push(`${path}.on: unknown part '${String(a.on).slice(0, 20)}' for ${tpl}, dropped`); return null; }
  }
  let shape = typeof a.shape === 'string' ? a.shape.toLowerCase() : 'box';
  if (!SHAPES.includes(shape)) { const al = has(SHAPE_ALIAS, shape) ? SHAPE_ALIAS[shape] : 'box'; errors.push(`${path}.shape: '${String(a.shape).slice(0, 20)}' -> '${al}'`); shape = al; }
  const out = { on, shape };
  // game format: an absent at/size keeps the compiler default (at [0,0,0], size [1,1,1] = the whole part)
  if (isWire || a.at !== undefined) {
    const at = vec(a.at, [0, 0, 0], AT_RANGE[0], AT_RANGE[1]);
    if (at.bad) errors.push(`${path}.at: repaired/clamped to [${at.v.join(', ')}]`);
    out.at = at.v.map(round4);
  }
  if (isWire || a.size !== undefined) {
    const size = vec(a.size, [0.3, 0.3, 0.3], SIZE_RANGE[0], SIZE_RANGE[1]);
    if (size.bad) errors.push(`${path}.size: repaired/clamped to [${size.v.join(', ')}]`);
    out.size = size.v.map(round4);
  }
  if (a.axis !== undefined && a.axis !== 'y') {
    if (a.axis === 'x' || a.axis === 'z') out.axis = a.axis; else errors.push(`${path}.axis: invalid, dropped`);
  }
  if (a.rot !== undefined && a.rot !== null) {
    const r = vec(a.rot, [0, 0, 0], -180, 180);
    if (r.bad) errors.push(`${path}.rot: repaired/clamped`);
    const rv = r.v.map(round4);
    if (rv.some(v => v !== 0)) out.rot = rv;
  }
  if (a.mirror === true) out.mirror = true;
  if (a.glow === true) out.glow = true;
  if (isWire) {
    const w = wireColor(a.color, a.pattern, a.color2, a.period, path, errors, '#888888');
    out.color = w.color;
    if (w.stripes) out.stripes = w.stripes;
    if (w.checker) out.checker = w.checker;
  } else {
    out.color = normColorValue(a.color, `${path}.color`, errors, '#888888');
    if (a.stripes !== undefined) {
      const s = a.stripes, sc = parseColor(isObj(s) ? s.color : s);
      if (!sc) errors.push(`${path}.stripes: invalid, dropped`);
      else {
        const st = { color: sc };
        if (isObj(s) && s.axis !== undefined) { if (['x', 'y', 'z'].includes(s.axis)) st.axis = s.axis; else errors.push(`${path}.stripes.axis: invalid, dropped`); }
        if (isObj(s) && s.period !== undefined) { const p = toNum(s.period); if (p && p > 0) { st.period = round4(clamp(p, 0.02, 0.5)); if (st.period !== p) errors.push(`${path}.stripes.period: clamped`); } else errors.push(`${path}.stripes.period: invalid, dropped`); }
        out.stripes = st;
      }
    }
    if (a.checker !== undefined) { const k = parseColor(a.checker); if (k) out.checker = k; else errors.push(`${path}.checker: invalid, dropped`); }
  }
  return out;
}

const BASE_LIMIT = 40000; // estimate; the real base count stays below ~32000
function enforceBaseBudget(rec, errors) {
  if (rec.template !== 'quadruped' || baseCost(rec) <= BASE_LIMIT) return;
  const b = rec.build, spec = UNIT_TEMPLATES.quadruped.build;
  for (let i = 0; i < 40 && baseCost(rec) > BASE_LIMIT; i++) {
    for (const k of ['headScale', 'jaw', 'bulk', 'tail']) if (b[k] !== 0) b[k] = round4(Math.max(spec[k][0], (b[k] ?? 1) * 0.9));
    b.height = round4(Math.max(spec.height[0], (b.height ?? 2) * 0.95));
    if (b.length > 3) b.length = round4(Math.max(3, b.length * 0.97));
  }
  errors.push('build: body too big for the voxel budget, proportions reduced');
}
function enforceBudget(rec, errors) {
  enforceBaseBudget(rec, errors);
  let total = estimateUnitVoxels(rec);
  if (total <= VOXEL_BUDGET) return;
  const base = baseCost(rec);
  for (let iter = 0; iter < 6 && total > VOXEL_BUDGET; iter++) {
    const addCost = total - base;
    const f = Math.cbrt(Math.max(0.001, (VOXEL_BUDGET - base) / Math.max(1, addCost))) * 0.97;
    for (const a of rec.add) a.size = (a.size || [1, 1, 1]).map(v => round4(Math.max(SIZE_RANGE[0], v * f)));
    total = estimateUnitVoxels(rec);
  }
  while (total > VOXEL_BUDGET && rec.add.length) {
    let bi = 0, bc = -1;
    rec.add.forEach((a, i) => { const c = entryCost(rec, a); if (c > bc) { bc = c; bi = i; } });
    rec.add.splice(bi, 1);
    total = estimateUnitVoxels(rec);
  }
  errors.push(`add: too many voxels, sizes scaled down to fit the budget`);
}

function inferTemplate(input) {
  const add = Array.isArray(input.add) ? input.add : [];
  const ons = new Set(add.map(a => isObj(a) ? a.on : null));
  const b = isObj(input.build) ? input.build : {};
  if (ons.has('segments') || toNum(b.segments) > 0 || toNum(b.radius) > 0) return 'serpent';
  if (ons.has('body') || ons.has('neck') || toNum(b.legLength) > 0 || toNum(b.bulk) > 0) return 'quadruped';
  return 'humanoid';
}

/**
 * Validate and repair a unit recipe. Never throws.
 * @param {any} input  game-format or wire-format recipe (or garbage)
 * @param {{templates?: string[], fallbackName?: string}} [opts]  templates: allowed templates (others are converted)
 * @returns {{ ok: boolean, recipe: object, errors: string[] }}
 *   ok=false means the input was not usable as a whole (not an object, unknown template, or most
 *   parts dropped); `recipe` is still a valid, compilable recipe in every case.
 */
export function validateUnitRecipe(input, opts = {}) {
  const errors = [];
  try {
    let allowed = Array.isArray(opts.templates) ? opts.templates.filter(t => has(UNIT_TEMPLATES, t)) : TEMPLATE_NAMES;
    if (!allowed.length) allowed = TEMPLATE_NAMES;
    if (typeof input === 'string') { try { input = JSON.parse(input); } catch { errors.push('recipe: not valid JSON'); } }
    if (!isObj(input)) {
      errors.push('recipe: not an object');
      return { ok: false, recipe: fallbackUnitRecipe(opts.fallbackName || ''), errors };
    }
    let fatal = false;
    let tpl = input.template;
    if (!has(UNIT_TEMPLATES, tpl)) {
      const g = inferTemplate(input);
      errors.push(`template: '${String(tpl).slice(0, 20)}' unknown -> '${g}'`);
      tpl = g; fatal = true;
    }
    if (!allowed.includes(tpl)) {
      const to = allowed.includes('humanoid') ? 'humanoid' : allowed[0];
      errors.push(`template: '${tpl}' not supported here -> '${to}'`);
      tpl = to;
    }
    const isWire = Array.isArray(input.colors) || (Array.isArray(input.add) && input.add.some(a => typeof a === 'string' || (isObj(a) && ('pattern' in a || 'color2' in a))));
    const rec = {
      name: cleanName(input.name, opts.fallbackName ? cleanName(opts.fallbackName, 'Einheit') : 'Einheit'),
      template: tpl,
      build: normBuild(input.build, tpl, errors, isWire),
      colors: normColors(input.colors, tpl, errors),
      add: [],
    };
    const mount = normMount(input.mount, tpl, errors);
    if (mount) rec.mount = mount;
    if (KAMPF.includes(input.kampf)) rec.kampf = input.kampf; // optional: older recipes get it from the name (combatRole.js)
    const face = normFace(input.face, errors), hair = normHair(input.hair, errors), hat = normHat(input.hat, errors);
    if (face) rec.face = face;
    if (hair) rec.hair = hair;
    if (hat) rec.hat = hat;
    if (typeof input.name !== 'string' || !input.name.trim()) errors.push('name: missing');
    else if (rec.name !== input.name) errors.push('name: cleaned/truncated');
    const src = Array.isArray(input.add) ? input.add : [];
    if (input.add !== undefined && !Array.isArray(input.add)) errors.push('add: not an array, ignored');
    if (src.length > MAX_ADD) errors.push(`add: ${src.length} entries, truncated to ${MAX_ADD}`);
    let dropped = 0;
    for (let i = 0; i < src.length && rec.add.length < MAX_ADD; i++) {
      const a = normAdd(src[i], i, tpl, errors, isWire);
      if (a) rec.add.push(a); else dropped++;
    }
    if (dropped > 2 && dropped * 2 > Math.min(src.length, MAX_ADD)) fatal = true;
    enforceBudget(rec, errors);
    return { ok: !fatal, recipe: rec, errors };
  } catch (e) {
    errors.push('internal: ' + String(e && e.message || e).slice(0, 80));
    return { ok: false, recipe: fallbackUnitRecipe(opts.fallbackName || ''), errors };
  }
}

/* ---------------- game format -> wire format (for few-shot examples) ---------------- */
function toWireColor(v) {
  if (typeof v === 'string') return { color: v, pattern: 'none', color2: v };
  if (v.checker) return { color: v.color, pattern: 'checker', color2: v.checker };
  if (v.stripes) return { color: v.color, pattern: 'stripes_y', color2: v.stripes };
  return { color: v.color, pattern: 'none', color2: v.color };
}
/** Convert a game-format recipe to the LLM wire format (every key present, colours/add as compact strings). */
export function toWireUnit(rec) {
  const rb = rec.build || {};
  const quad = rec.template === 'quadruped';
  const build = Object.fromEntries(ALL_BUILD_KEYS.map(k => [k, k === 'armForward' ? !!rb[k] : k === 'headShape' ? (rb[k] || 'box')
    : quad && rb[k] === 0 && has(WIRE_ZERO, k) ? WIRE_ZERO[k] : (rb[k] || 0)]));
  const mount = rec.mount ? { on: rec.mount.on, at: rec.mount.at || [0, 0, 1] } : { on: 'none', at: [0, 0, 0] };
  const colors = Object.entries(rec.colors || {}).map(([part, v]) => {
    const c = toWireColor(v);
    return `${part} ${c.color}` + (c.pattern === 'checker' ? ` checker:${c.color2}` : c.pattern === 'stripes_y' ? ` stripes:${c.color2}` : '');
  });
  const add = (rec.add || []).map(a => {
    const c = toWireColor(a.color);
    let s = `${a.on} ${a.shape || 'box'} ${(a.at || [0, 0, 0]).join(' ')} ${(a.size || [1, 1, 1]).join(' ')} ${c.color}`;
    if (a.mirror) s += ' mirror';
    if (a.glow) s += ' glow';
    if (a.rot) s += ` rot:${a.rot.join(',')}`;
    if (a.axis && a.axis !== 'y') s += ` axis:${a.axis}`;
    if (a.stripes) s += ` stripes:${a.stripes.axis || 'y'}:${a.stripes.color}:${a.stripes.period || 0.05}`;
    else if (a.checker) s += ` checker:${a.checker}`;
    else if (c.pattern === 'checker') s += ` checker:${c.color2}`;
    else if (c.pattern === 'stripes_y') s += ` stripes:y:${c.color2}:0.05`;
    return s;
  });
  // face / hair / hat: every key present; 'auto' / '' keep the template defaults
  const f = rec.face || {};
  const face = {
    ...Object.fromEntries(FACE_ENUM_KEYS.map(k => [k, typeof f[k] === 'string' ? f[k] : 'auto'])),
    marks: Array.isArray(f.marks) ? f.marks.slice() : [],
    ...Object.fromEntries(FACE_COLOR_KEYS.map(k => [k, typeof f[k] === 'string' ? f[k] : ''])),
  };
  const hair = { style: rec.hair && rec.hair.style || 'keine', color: rec.hair && rec.hair.color || '' };
  const hat = { type: rec.hat && rec.hat.type || 'keiner', color: rec.hat && rec.hat.color || '', color2: rec.hat && rec.hat.color2 || '' };
  return { name: rec.name, template: rec.template, ...(KAMPF.includes(rec.kampf) ? { kampf: rec.kampf } : {}), build, colors, face, hair, hat, mount, add };
}

/* ---------------- fallback: always works, deterministic per prompt ---------------- */
function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
const FB_PALETTES = [
  ['#5f6b3a', '#4c5730', '#2b2e33'], ['#3d5a80', '#2a3f5f', '#1c1f24'], ['#8a5a33', '#6b4426', '#3a2a22'],
  ['#9aa3ad', '#6f7882', '#3a3f47'], ['#7a3ab8', '#4b2470', '#1c1f24'], ['#c8102e', '#7a0a1c', '#16161b'],
  ['#1f8a8a', '#17706f', '#0f3f3f'], ['#d9b23c', '#a8862a', '#5a4a1a'],
];
const SKINS = ['#e3b48a', '#c4926a', '#a8703f', '#f0c8a0', '#8d5a3a', '#f2c9a8'];
const SERPENT_WORDS = /schlange|snake|serpent|wurm|worm|drache|dragon|aal|eel|kraken|seemonster|leviathan|hydra|python|kobra|cobra|natter|viper|basilisk|lindwurm/i;
/**
 * Deterministic, always-valid placeholder recipe for a prompt (used offline, on refusal or errors).
 * @param {string} prompt
 */
export function fallbackUnitRecipe(prompt) {
  const p = typeof prompt === 'string' ? prompt.slice(0, 200) : '';
  const h = hashStr(p.toLowerCase());
  const pal = FB_PALETTES[h % FB_PALETTES.length];
  const name = cleanName(p, 'Unbekannt');
  if (SERPENT_WORDS.test(p)) {
    return { name, template: 'serpent', build: { length: 9, radius: 0.85, segments: 8 },
      colors: { body: pal[0], belly: '#d8d0b0', head: pal[0], jaw: pal[1] },
      add: [
        { on: 'segments', shape: 'box', at: [0, 1.05, 0], size: [0.09, 0.5, 0.6], color: 'team' },
        { on: 'head', shape: 'ellipsoid', at: [0.7, 0.45, 0.35], size: [0.22, 0.22, 0.22], mirror: true, color: '#ffd23c', glow: true },
        { on: 'jaw', shape: 'box', at: [0, 0.75, 0.1], size: [0.75, 0.2, 0.85], color: '#6a1a2a' },
      ] };
  }
  const skin = SKINS[(h >>> 8) % SKINS.length];
  const hat = (h >>> 12) % 3;
  const add = [
    { on: 'head', shape: 'box', at: [0.38, 0.05, 1.0], size: [0.12, 0.1, 0.06], mirror: true, color: '#1c1f24' },
    { on: 'torso', shape: 'box', at: [0, -0.05, 1.0], size: [0.38, 0.9, 0.1], color: 'team' },
    { on: 'torso', shape: 'box', at: [0, -0.8, 0], size: [1.05, 0.1, 1.05], color: pal[2] },
  ];
  if (hat === 0) add.push({ on: 'head', shape: 'box', at: [0, 0.75, -0.05], size: [1.15, 0.42, 1.18], color: pal[1] });
  else if (hat === 1) add.push({ on: 'head', shape: 'cone', at: [0, 1.4, 0], size: [0.9, 0.7, 0.9], color: pal[0] });
  else add.push({ on: 'head', shape: 'box', at: [0, 0.9, -0.08], size: [1.1, 0.3, 1.12], color: pal[2] });
  return { name, template: 'humanoid', build: { width: round4(0.9 + ((h >>> 16) % 5) * 0.08) },
    colors: { skin, torso: pal[0], pelvis: pal[1], arms: pal[0], hands: skin, legs: pal[1], feet: pal[2] }, add };
}

/* ---------------- safe compile ---------------- */
function countVoxels(m) {
  if (!m || typeof m !== 'object') return -1;
  if (m.map instanceof Map) return m.map.size;
  if (typeof m.voxN === 'number') return m.voxN;
  if (typeof m.count === 'number') return m.count;
  if (Array.isArray(m.voxels)) return m.voxels.length;
  if (m.voxels && typeof m.voxels.length === 'number') return m.voxels.length;
  if (m.model && m.model.map instanceof Map) return m.model.map.size;
  return 0;
}
/**
 * Compile a recipe with the given compiler inside try/catch and reject oversized or broken results.
 * @param {object} recipe
 * @param {(recipe: object) => any} recipeModelFn  e.g. r => recipeModel(r) or r => compileBuilding(r, 1)
 * @param {{maxVoxels?: number}} [opts]
 * @returns {{ ok: boolean, model?: any, voxels: number, ms: number, error?: string }}
 */
export function safeCompile(recipe, recipeModelFn, { maxVoxels = 60000 } = {}) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const ms = () => Math.round(((typeof performance !== 'undefined' ? performance : Date).now() - t0) * 10) / 10;
  try {
    if (typeof recipeModelFn !== 'function') return { ok: false, voxels: 0, ms: 0, error: 'no compiler' };
    const model = recipeModelFn(recipe);
    const voxels = countVoxels(model);
    if (voxels < 0) return { ok: false, voxels: 0, ms: ms(), error: 'compiler returned nothing' };
    if (model.map instanceof Map) {
      if (voxels === 0) return { ok: false, voxels, ms: ms(), error: 'empty model' };
      for (const v of model.map.values()) {
        if (!v || !Number.isFinite(v.c)) return { ok: false, voxels, ms: ms(), error: 'invalid voxel colour' };
      }
    }
    if (voxels > maxVoxels) return { ok: false, voxels, ms: ms(), error: `too many voxels (${voxels} > ${maxVoxels})` };
    return { ok: true, model, voxels, ms: ms() };
  } catch (e) {
    return { ok: false, voxels: 0, ms: ms(), error: 'compile threw: ' + String(e && e.message || e).slice(0, 120) };
  }
}
